<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import { DEFAULT_LOCALE, type Locale } from '@/i18n'
import { staticPageHref } from '@/router/products'
import { useAuth } from '@/composables/useAuth'
import { useReimbursements, type ReimbursementRow } from '@/composables/useReimbursements'
import { formatEur } from '@/composables/useSubscription'
import AccountLayout from '@/components/layout/AccountLayout.vue'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const { member } = useAuth()
const { reimbursements, summary, loading, failed, reload } = useReimbursements()

const currentLocale = computed<Locale>(() => (route.meta.locale as Locale) ?? DEFAULT_LOCALE)
const loginHref = computed(() => staticPageHref('login', currentLocale.value))
useHead({ title: computed(() => `${t('auth.account.reimbursements.title')} — MySmerra`) })

/** Accès réservé. */
onMounted(() => {
  if (!member.value) router.replace(loginHref.value)
})

/* --- Selected reimbursement: none until the user clicks a row, so the detail
   panel only shows up from the first selection onwards. --- */
const selectedId = ref<string | null>(null)
const selected = computed<ReimbursementRow | null>(
  () => reimbursements.value.find((r) => r.id === selectedId.value) ?? null,
)

/* Sur mobile, la maquette bascule sur un écran de détail : le panneau remplace
   la liste tant qu'il est ouvert. Sur desktop les deux colonnes cohabitent, la
   classe est neutralisée par la media query. */
const detailOpen = ref(false)

/* --- Multi-act rows: their acts expand underneath them in the statement.
   Selection and expansion go together — a single row is open at a time, and it
   is always the selected one. --- */
const expandedId = ref<string | null>(null)

/** Selects a reimbursement; any other row's acts collapse. */
function selectRow(row: ReimbursementRow) {
  selectedId.value = row.id
  expandedId.value = row.isMultiAct ? row.id : null
}

/** Row click: selection + switch to the detail screen (mobile). */
function select(row: ReimbursementRow) {
  selectRow(row)
  detailOpen.value = true
}

/** Chevron: on another row it selects and expands it; on the current row it
 *  only collapses/expands its acts. It never switches to the detail screen,
 *  which would hide the very acts the user just asked to see. */
function toggleActs(row: ReimbursementRow) {
  if (!selected.value || selected.value.id !== row.id) {
    selectRow(row)
    return
  }
  expandedId.value = expandedId.value === row.id ? null : row.id
}

/** Bloc « Détails du soin » du panneau : déplié, et re-déplié à chaque changement
 *  de sélection pour que le nouveau remboursement s'ouvre toujours complet. */
const actsOpen = ref(true)
watch(selected, () => (actsOpen.value = true))

/** Date ISO → format court localisé. */
function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(locale.value, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

/** Montant € du relevé : toujours affiché avec les centimes. */
function fmtAmount(value: number): string {
  return formatEur(value, locale.value, true)
}

/** Libellé d'une ligne : un multi-actes annonce son nombre d'actes ; un décompte
 *  sans acte détaillé retombe sur un libellé générique. */
function rowLabel(row: ReimbursementRow): string {
  if (row.isMultiAct) return t('auth.account.reimbursements.multiAct', { count: row.acts.length })
  return row.label || t('auth.account.reimbursements.unnamedCare')
}

/** Largeur d'un segment de la barre de répartition, en % du coût du soin. */
function share(value: number, total: number): string {
  return total > 0 ? `${(value / total) * 100}%` : '0%'
}
</script>

<template>
  <AccountLayout v-if="member" active="reimbursements">
    <section class="rb" :class="{ 'is-detail-open': detailOpen }">
      <!-- Fil d'ariane + titre -->
      <nav class="rb__crumb" aria-label="breadcrumb">
        <span>{{ t('auth.account.reimbursements.breadcrumbHome') }}</span>
        <span class="rb__crumb-sep" aria-hidden="true">›</span>
        <span class="rb__crumb-current">{{ t('auth.account.reimbursements.title') }}</span>
      </nav>
      <header class="rb__head">
        <h1 class="rb__title">{{ t('auth.account.reimbursements.title') }}</h1>
        <p class="rb__subtitle">{{ t('auth.account.reimbursements.subtitle') }}</p>
      </header>

      <div class="rb__grid">
        <!-- Colonne gauche : récapitulatif + relevé -->
        <div class="rb__list-col">
          <h2 class="rb__section-title">{{ t('auth.account.reimbursements.listTitle') }}</h2>

          <!-- Récapitulatif du mois en cours. Masqué tant que le relevé n'est
               pas chargé : des totaux à zéro se liraient comme une absence de
               remboursement. -->
          <div v-if="!loading && !failed" class="sum">
            <div class="sum__item">
              <span class="sum__label">{{ t('auth.account.reimbursements.summary.reimbursed') }}</span>
              <span class="sum__value">{{ fmtAmount(summary.reimbursed) }}</span>
              <span class="sum__hint">{{ t('auth.account.reimbursements.summary.period') }}</span>
            </div>
            <div class="sum__item">
              <span class="sum__label">{{ t('auth.account.reimbursements.summary.smerra') }}</span>
              <span class="sum__value">{{ fmtAmount(summary.smerra) }}</span>
            </div>
            <div class="sum__item">
              <span class="sum__label">{{ t('auth.account.reimbursements.summary.socialSecurity') }}</span>
              <span class="sum__value">{{ fmtAmount(summary.socialSecurity) }}</span>
            </div>
            <div class="sum__item">
              <span class="sum__label">{{ t('auth.account.reimbursements.summary.remaining') }}</span>
              <span class="sum__value">{{ fmtAmount(summary.remaining) }}</span>
            </div>
          </div>

          <!-- Relevé : en-têtes de colonnes (desktop) puis une ligne par soin -->
          <p v-if="loading" class="rb__state">{{ t('auth.account.reimbursements.loading') }}</p>

          <div v-else-if="failed" class="rb__error">
            <p>{{ t('auth.account.reimbursements.loadError') }}</p>
            <button type="button" class="rb__retry" @click="reload">
              {{ t('auth.account.reimbursements.retry') }}
            </button>
          </div>

          <div v-else-if="reimbursements.length" class="rlist">
            <div class="rlist__head" aria-hidden="true">
              <span>{{ t('auth.account.reimbursements.table.careDate') }}</span>
              <span>{{ t('auth.account.reimbursements.table.careNature') }}</span>
              <span>{{ t('auth.account.reimbursements.table.paid') }}</span>
              <span>{{ t('auth.account.reimbursements.table.socialSecurityShare') }}</span>
              <span>{{ t('auth.account.reimbursements.table.smerraShare') }}</span>
              <span>{{ t('auth.account.reimbursements.table.reimbursed') }}</span>
              <span>{{ t('auth.account.reimbursements.table.status') }}</span>
            </div>

            <ul class="rlist__body">
              <li v-for="r in reimbursements" :key="r.id" class="rlist__item">
                <!-- Le bouton de sélection couvre toute la ligne : les cellules
                     laissent passer le clic, seul le dépli des actes l'intercepte. -->
                <div class="rrow" :class="{ 'is-selected': selected && selected.id === r.id }">
                  <!-- Le titre porte le libellé complet : les cellules ne
                       reçoivent pas le survol (elles laissent passer le clic). -->
                  <button
                    type="button"
                    class="rrow__select"
                    :title="rowLabel(r)"
                    :aria-pressed="!!selected && selected.id === r.id"
                    :aria-expanded="r.isMultiAct ? expandedId === r.id : undefined"
                    @click="select(r)"
                  >
                    <span class="sr-only">{{ rowLabel(r) }} — {{ fmtDate(r.careDate) }}</span>
                  </button>

                  <span class="rrow__cell rrow__date">{{ fmtDate(r.careDate) }}</span>
                  <span class="rrow__cell rrow__label">
                    <span class="rrow__label-text">{{ rowLabel(r) }}</span>
                    <button
                      v-if="r.isMultiAct"
                      type="button"
                      class="rrow__toggle"
                      :aria-expanded="expandedId === r.id"
                      :aria-label="t('auth.account.reimbursements.table.toggleActs')"
                      @click="toggleActs(r)"
                    >
                      <svg
                        class="rrow__toggle-icon"
                        :class="{ 'is-open': expandedId === r.id }"
                        viewBox="0 0 20 20"
                        width="14"
                        height="14"
                        aria-hidden="true"
                      >
                        <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                  </span>
                  <span class="rrow__cell rrow__num">{{ fmtAmount(r.cost) }}</span>
                  <span class="rrow__cell rrow__num rrow__num--sec">{{ fmtAmount(r.socialSecurity) }}</span>
                  <span class="rrow__cell rrow__num rrow__num--sec">{{ fmtAmount(r.smerra) }}</span>
                  <span class="rrow__cell rrow__num rrow__num--sec">{{ fmtAmount(r.reimbursed) }}</span>
                  <span class="rrow__cell rrow__status">
                    <span class="badge" :class="`badge--${r.status}`">
                      {{ t(`auth.account.reimbursements.status.${r.status}`) }}
                    </span>
                  </span>
                  <span class="rrow__cell rrow__chevron" aria-hidden="true">
                    <svg viewBox="0 0 20 20" width="14" height="14">
                      <path d="M8 5l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                </div>

                <!-- Actes du multi-actes, dépliés sous la ligne et calés sur
                     les mêmes colonnes qu'elle. -->
                <ul v-if="r.isMultiAct && expandedId === r.id" class="subacts">
                  <li v-for="act in r.acts" :key="act.label" class="subacts__row">
                    <span class="subacts__label" :title="act.label">{{ act.label }}</span>
                    <span class="subacts__num">{{ fmtAmount(act.cost) }}</span>
                    <span class="subacts__num subacts__num--sec">{{ fmtAmount(act.socialSecurity) }}</span>
                    <span class="subacts__num subacts__num--sec">{{ fmtAmount(act.smerra) }}</span>
                    <span class="subacts__num subacts__num--sec">{{ fmtAmount(act.reimbursed) }}</span>
                    <span class="subacts__status">
                      <span class="badge" :class="`badge--${act.status}`">
                        {{ t(`auth.account.reimbursements.status.${act.status}`) }}
                      </span>
                    </span>
                  </li>
                </ul>
              </li>
            </ul>
          </div>

          <p v-else class="rb__empty">{{ t('auth.account.reimbursements.empty') }}</p>
        </div>

        <!-- Colonne droite : détail du remboursement sélectionné -->
        <aside v-if="selected" class="rb__detail">
          <button type="button" class="rb__back" @click="detailOpen = false">
            <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
              <path d="M12 5l-5 5 5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            {{ t('auth.account.reimbursements.detail.back') }}
          </button>

          <div class="dpanel">
            <h2 class="dpanel__title">{{ t('auth.account.reimbursements.detail.title') }}</h2>
            <span class="badge" :class="`badge--${selected.status}`">
              {{ t(`auth.account.reimbursements.status.${selected.status}`) }}
            </span>
            <p class="dpanel__care">{{ rowLabel(selected) }}</p>
            <p class="dpanel__date">
              {{ t('auth.account.reimbursements.detail.on', { date: fmtDate(selected.careDate) }) }}
            </p>

            <!-- Versement de la mutuelle -->
            <div class="dcard">
              <p class="dcard__label">{{ t('auth.account.reimbursements.detail.paidByMutual') }}</p>
              <p class="dcard__amount">{{ fmtAmount(selected.smerra) }}</p>
            </div>

            <!-- Coût du soin et répartition de la prise en charge -->
            <div class="dcard dcard--breakdown">
              <div class="dline">
                <span class="dline__label">{{ t('auth.account.reimbursements.detail.totalCost') }}</span>
                <span class="dline__value">{{ fmtAmount(selected.cost) }}</span>
              </div>
              <p class="dcard__label">{{ t('auth.account.reimbursements.detail.breakdown') }}</p>
              <div class="bar">
                <span class="bar__seg bar__seg--social" :style="{ width: share(selected.socialSecurity, selected.cost) }"></span>
                <span class="bar__seg bar__seg--smerra" :style="{ width: share(selected.smerra, selected.cost) }"></span>
                <span class="bar__seg bar__seg--remaining" :style="{ width: share(selected.remaining, selected.cost) }"></span>
              </div>
              <div class="dline">
                <span class="dline__label">
                  <span class="dot dot--social" aria-hidden="true"></span>
                  {{ t('auth.account.reimbursements.detail.socialSecurity') }}
                </span>
                <span class="dline__value">{{ fmtAmount(selected.socialSecurity) }}</span>
              </div>
              <div class="dline">
                <span class="dline__label">
                  <span class="dot dot--smerra" aria-hidden="true"></span>
                  {{ t('auth.account.reimbursements.detail.smerra') }}
                </span>
                <span class="dline__value">{{ fmtAmount(selected.smerra) }}</span>
              </div>
              <div class="dline">
                <span class="dline__label">
                  <span class="dot dot--remaining" aria-hidden="true"></span>
                  {{ t('auth.account.reimbursements.detail.remaining') }}
                </span>
                <span class="dline__value">{{ fmtAmount(selected.remaining) }}</span>
              </div>
              <div class="dline dline--total">
                <span class="dline__label">{{ t('auth.account.reimbursements.detail.totalReimbursed') }}</span>
                <span class="dline__value">{{ fmtAmount(selected.reimbursed) }}</span>
              </div>
            </div>

            <!-- Détail acte par acte (multi-actes uniquement) -->
            <div v-if="selected.isMultiAct" class="dcard dcard--acts">
              <button
                type="button"
                class="dcard__toggle"
                :aria-expanded="actsOpen"
                @click="actsOpen = !actsOpen"
              >
                {{ t('auth.account.reimbursements.detail.actsTitle') }}
                <svg
                  class="dcard__toggle-icon"
                  :class="{ 'is-open': actsOpen }"
                  viewBox="0 0 20 20"
                  width="16"
                  height="16"
                  aria-hidden="true"
                >
                  <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              <ul v-if="actsOpen" class="dacts">
                <li v-for="act in selected.acts" :key="act.label" class="dacts__row">
                  <span class="dacts__label">{{ act.label }}</span>
                  <span class="dacts__cost">{{ fmtAmount(act.cost) }}</span>
                  <span class="dacts__sub">
                    {{ t('auth.account.reimbursements.detail.actSocialSecurity', { amount: fmtAmount(act.socialSecurity) }) }}
                  </span>
                  <span class="dacts__sub dacts__sub--end">
                    {{ t('auth.account.reimbursements.detail.actSmerra', { amount: fmtAmount(act.smerra) }) }}
                  </span>
                  <span class="dacts__sub">
                    {{ t('auth.account.reimbursements.detail.actReimbursed', { amount: fmtAmount(act.reimbursed) }) }}
                  </span>
                  <span class="dacts__sub dacts__sub--end">
                    {{ t('auth.account.reimbursements.detail.actRemaining', { amount: fmtAmount(act.remaining) }) }}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </aside>
      </div>
    </section>
  </AccountLayout>
</template>

<style scoped>
.rb {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  /* La sécurité sociale est en bleu sur la maquette : tokens.css n'a pas de
     bleu, la valeur est donc portée localement par cet écran. */
  --rb-social: #3799fc;
  /* Gabarit du relevé, partagé par l'en-tête, les lignes et les sous-actes :
     c'est ce qui garantit que les trois tombent sur les mêmes colonnes.
     La date est un peu plus large que son contenu pour dégager la nature du
     soin, le `gap` de la grille étant uniforme. */
  --rb-status-w: 6.25rem;
  --rb-columns: 5.1rem minmax(0, 1fr) repeat(4, 3.75rem) var(--rb-status-w) 0.9rem;
}

/* Fil d'ariane + titre */
.rb__crumb { display: flex; align-items: center; gap: 0.4rem; font-size: var(--text-sm); color: var(--text-muted); }
.rb__crumb-current { color: var(--color-chocolate); font-weight: 600; }
.rb__title { font-size: var(--text-xl); font-weight: 700; color: var(--color-chocolate); }
.rb__subtitle { margin-top: 0.15rem; font-size: var(--text-sm); color: var(--text-muted); }

/* Grille relevé / détail */
/* Le détail est une colonne de largeur fixe : tout l'espace restant va au
   relevé, qui en a besoin pour ses sept colonnes de montants. */
.rb__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 17rem;
  gap: 1.25rem;
  align-items: start;
  margin-top: 0.25rem;
}
.rb__list-col {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  background: var(--color-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  padding: 1.25rem;
}
.rb__section-title { font-size: var(--text-lg); font-weight: 700; color: var(--color-chocolate); }
.rb__empty,
.rb__state { font-size: var(--text-sm); color: var(--text-muted); }

/* Échec du chargement du relevé : message + nouvelle tentative. */
.rb__error {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 1rem;
  border: 1px solid color-mix(in oklab, var(--color-cherry) 30%, white);
  border-radius: var(--radius-lg);
  background: color-mix(in oklab, var(--color-cherry) 7%, white);
  font-size: var(--text-sm);
  color: var(--color-chocolate);
}
.rb__retry {
  padding: 0.5rem 1.1rem;
  border: 0;
  border-radius: var(--radius-pill);
  background: var(--color-chocolate);
  color: var(--color-white);
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-out);
}
.rb__retry:hover { background: color-mix(in oklab, var(--color-chocolate) 85%, white); }

/* Récapitulatif du mois en cours */
/* Trois rangées partagées (intitulé / montant / précision) : un intitulé qui
   passe sur deux lignes ne décale plus le montant des trois autres colonnes. */
.sum {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: auto auto auto;
  gap: 0.75rem;
  padding: 0.9rem 1rem;
  background: var(--bg-pink-soft);
  border-radius: var(--radius-lg);
}
.sum__item {
  display: grid;
  grid-row: span 3;
  grid-template-rows: subgrid;
  align-content: start;
  gap: 0.15rem;
  min-width: 0;
}
.sum__label { font-size: var(--text-xs); color: var(--text-muted); }
.sum__value { font-size: var(--text-lg); font-weight: 700; color: var(--color-chocolate); }
.sum__hint { font-size: var(--text-xs); color: var(--text-muted); }

/* Relevé */
.rlist { display: flex; flex-direction: column; gap: 0.4rem; }
/* L'en-tête et chaque ligne sont des grids distincts : seules des largeurs
   fixes les gardent alignés (une largeur calculée sur le contenu donnerait des
   colonnes différentes d'un conteneur à l'autre). Seule la nature du soin est
   flexible et absorbe l'espace restant. Les intitulés, plus longs que les
   valeurs, passent sur deux lignes comme sur la maquette. */
.rlist__head,
.rrow {
  display: grid;
  grid-template-columns: var(--rb-columns);
  gap: 0.5rem;
  align-items: center;
}
.rlist__head {
  padding: 0 0.75rem;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.2;
  color: var(--text-muted);
}
.rlist__body { display: flex; flex-direction: column; }
.rlist__item { border-bottom: 1px solid var(--border); }
.rlist__item:last-child { border-bottom: 0; }

.rrow {
  position: relative;
  padding: 0.7rem 0.75rem;
  border-radius: var(--radius-md);
  /* Relevé dense : texte plus petit que le corps de page, comme sur la maquette,
     pour que les sept colonnes tiennent sans tronquer la nature du soin. */
  font-size: var(--text-xs);
  color: var(--color-chocolate);
  transition: background var(--dur-fast) var(--ease-out);
}
/* Only the main row carries the highlight: expanded acts live outside `.rrow`
   and keep the statement's background. */
.rrow.is-selected { background: color-mix(in oklab, var(--color-peach) 24%, white); }
/* Zone cliquable de la ligne, sous les cellules. */
.rrow__select {
  position: absolute;
  inset: 0;
  border: 0;
  background: none;
  border-radius: inherit;
  cursor: pointer;
}
.rrow__select:focus-visible { outline: 2px solid var(--primary); outline-offset: -2px; }
/* Hover must not lighten the selected row: both rules share the same
   specificity, so the last one would win. */
.rrow:not(.is-selected):hover { background: color-mix(in oklab, var(--color-peach) 16%, white); }
/* Les cellules passent au-dessus du bouton mais lui laissent le clic. */
.rrow__cell { position: relative; pointer-events: none; min-width: 0; }
.rrow__date { font-weight: 600; white-space: nowrap; }
.rrow__label { display: flex; align-items: center; gap: 0.3rem; font-weight: 600; }
/* Les natures de soin sont du texte libre issu de Cimut, souvent longues
   (« Matériel et Appareil de Traitement Divers (Titre 1 chap. 3 LPP) »). Deux
   lignes maximum : au-delà la hauteur des lignes dérape, et le libellé complet
   reste accessible en infobulle. Même règle pour les actes, sinon le
   regroupement et son détail se comportent différemment. */
.rrow__label-text,
.subacts__label {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}
.rrow__num { font-variant-numeric: tabular-nums; white-space: nowrap; }
.rrow__num--sec { color: var(--text-muted); }
.rrow__status { display: flex; }
.rrow__chevron { display: flex; justify-content: flex-end; color: var(--text-muted); opacity: 0.55; }
/* Seul élément interactif au-dessus de la zone cliquable. */
.rrow__toggle {
  pointer-events: auto;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  padding: 0.15rem;
  border: 0;
  background: none;
  color: var(--text-muted);
  cursor: pointer;
}
.rrow__toggle:hover { color: var(--primary); }
.rrow__toggle-icon { transition: transform var(--dur-fast) var(--ease-out); }
.rrow__toggle-icon.is-open { transform: rotate(180deg); }

/* Actes dépliés sous une ligne multi-actes : même gabarit de colonnes et même
   padding que `.rrow`, pour qu'ils tombent exactement sous les colonnes du
   regroupement — libellé compris. La hiérarchie se lit à la graisse et à la
   couleur du texte, pas à un retrait qui casserait l'alignement. */
.subacts { display: flex; flex-direction: column; gap: 0.25rem; padding: 0 0.75rem 0.6rem; }
.subacts__row {
  display: grid;
  grid-template-columns: var(--rb-columns);
  gap: 0.5rem;
  align-items: center;
  font-size: var(--text-xs);
  color: var(--text-muted);
}
.subacts__label { grid-column: 2; font-weight: 600; color: var(--color-chocolate); }
.subacts__num { font-variant-numeric: tabular-nums; white-space: nowrap; }
.subacts__num--sec { color: var(--text-muted); }
.subacts__status { display: flex; }

/* Pastille de statut (même code couleur que Mes contrats) */
.badge {
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.55rem;
  border-radius: var(--radius-pill);
  font-size: var(--text-xs);
  font-weight: 600;
  white-space: nowrap;
}
.badge--total { color: #1a7f45; background: #e6f6ec; border: 1px solid color-mix(in oklab, #1a7f45 35%, white); }
.badge--partial { color: var(--primary); background: color-mix(in oklab, var(--color-peach) 22%, white); border: 1px solid var(--primary); }
/* Dans le relevé, les deux statuts occupent la même largeur — celle de leur
   colonne — pour que la colonne reste régulière d'une ligne à l'autre. */
.rrow__status .badge,
.subacts__status .badge {
  min-width: var(--rb-status-w);
  justify-content: center;
}

/* Panneau de détail */
.rb__detail { display: flex; flex-direction: column; gap: 0.6rem; }
/* Retour au relevé : uniquement pertinent sur l'écran de détail mobile. */
.rb__back {
  display: none;
  align-items: center;
  gap: 0.35rem;
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: none;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-chocolate);
  cursor: pointer;
}
.dpanel {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 1.25rem 1rem;
  background: var(--bg-pink-soft);
  border-radius: var(--radius-xl);
}
.dpanel__title { font-size: var(--text-base); font-weight: 700; color: var(--color-chocolate); }
.dpanel__care { font-size: var(--text-sm); font-weight: 600; color: var(--color-chocolate); }
.dpanel__date { margin-top: -0.35rem; font-size: var(--text-xs); color: var(--text-muted); }

.dcard {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  width: 100%;
  padding: 0.75rem 0.85rem;
  background: var(--color-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
}
.dcard--breakdown { gap: 0.5rem; padding-bottom: 0; }
.dcard--acts { padding: 0.35rem 0.85rem 0.6rem; }
.dcard__label { font-size: var(--text-xs); font-weight: 700; color: var(--color-chocolate); }
.dcard__amount { font-size: var(--text-lg); font-weight: 700; color: var(--color-chocolate); }
.dcard__toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.4rem 0;
  border: 0;
  background: none;
  font-size: var(--text-xs);
  font-weight: 700;
  color: var(--color-chocolate);
  cursor: pointer;
}
.dcard__toggle-icon { color: var(--text-muted); transition: transform var(--dur-fast) var(--ease-out); }
.dcard__toggle-icon.is-open { transform: rotate(180deg); }

.dline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: var(--text-xs);
  color: var(--color-chocolate);
}
.dline__label { display: flex; align-items: center; gap: 0.4rem; color: var(--text-muted); }
.dline__value { font-weight: 700; font-variant-numeric: tabular-nums; white-space: nowrap; }
/* Total remboursé : bandeau vert en pied de carte. */
.dline--total {
  margin: 0.35rem -0.85rem 0;
  padding: 0.55rem 0.85rem;
  background: #e6f6ec;
  border-top: 1px solid var(--border);
  border-radius: 0 0 var(--radius-lg) var(--radius-lg);
}
.dline--total .dline__label,
.dline--total .dline__value { color: #1a7f45; font-weight: 700; }

/* Barre de répartition sécurité sociale / Smerra / reste à charge */
.bar { display: flex; gap: 2px; height: 7px; border-radius: var(--radius-pill); overflow: hidden; }
.bar__seg { height: 100%; border-radius: var(--radius-pill); }
.bar__seg--social { background: var(--rb-social); }
.bar__seg--smerra { background: var(--primary); }
.bar__seg--remaining { background: var(--color-gray-500); }
.dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.dot--social { background: var(--rb-social); }
.dot--smerra { background: var(--primary); }
.dot--remaining { background: var(--color-gray-500); }

/* Détail acte par acte */
.dacts { display: flex; flex-direction: column; gap: 0.5rem; padding-bottom: 0.2rem; }
/* Trois rangées par acte : libellé + montant payé, puis les deux parts, puis
   remboursé / reste à charge. */
.dacts__row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.1rem 0.5rem;
  font-size: var(--text-xs);
}
.dacts__label { font-weight: 700; color: var(--color-chocolate); }
.dacts__cost { font-weight: 700; color: var(--color-chocolate); font-variant-numeric: tabular-nums; }
/* Un cran sous --text-xs : « Remboursé : 60,00 € » tient sur une ligne dans la
   colonne étroite du panneau. */
.dacts__sub { font-size: 0.7rem; color: var(--text-muted); }
.dacts__sub--end { text-align: right; }

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* Tablette : le relevé perd ses colonnes secondaires, le détail passe dessous. */
@media (max-width: 64rem) {
  .rb__grid { grid-template-columns: 1fr; }
  .sum { grid-template-columns: repeat(2, 1fr); }
}

/* Mobile : le relevé devient une liste de cartes et le détail un écran à part,
   comme sur la maquette (flèche de retour). */
@media (max-width: 48rem) {
  .rlist__head { display: none; }
  .rlist__item { border-bottom: 0; }
  .rrow {
    grid-template-columns: 1fr auto 1rem;
    gap: 0.15rem 0.6rem;
    margin-bottom: 0.5rem;
    background: var(--color-white);
    border: 1px solid var(--border);
  }
  .rrow__label { grid-column: 1; grid-row: 1; }
  .rrow__date { grid-column: 1; grid-row: 2; font-size: var(--text-xs); color: var(--text-muted); }
  .rrow__num { grid-column: 2; justify-self: end; }
  /* Seul le montant payé reste visible ; le reste est dans le détail. */
  .rrow__num--sec { display: none; }
  .rrow__status { grid-column: 2; grid-row: 2; }
  .rrow__chevron { grid-column: 3; grid-row: 1 / span 2; align-self: center; }
  /* Comme la ligne parente : seul le montant payé reste, le détail des parts
     est dans le panneau. Le padding de `.subacts` cale les libellés d'actes sur
     celui de la carte parente. */
  .subacts__row { grid-template-columns: 1fr auto auto; }
  .subacts__label { grid-column: 1; }
  .subacts__num--sec { display: none; }

  .rb__back { display: flex; }
  .rb.is-detail-open .rb__list-col { display: none; }
  .rb:not(.is-detail-open) .rb__detail { display: none; }
}
</style>
